import { useEffect, useRef } from "react";

/**
 * Auto-refresh hook: reruns `callback` at a fixed interval and also when the tab regains focus/visibility.
 * @param {Function} callback - the fetch/load function to invoke.
 * @param {number} intervalMs - polling interval in ms (default 30 s).
 * @param {boolean} enabled - toggle to disable polling.
 */
export function useAutoRefresh(callback, intervalMs = 30000, enabled = true) {
  const cbRef = useRef(callback);
  useEffect(() => { cbRef.current = callback; }, [callback]);

  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => {
      // Only refresh when tab is visible → saves bandwidth & prevents stale bursts.
      if (document.visibilityState === "visible") {
        cbRef.current?.();
      }
    };
    const id = setInterval(tick, intervalMs);

    const onVisible = () => {
      if (document.visibilityState === "visible") cbRef.current?.();
    };
    const onFocus = () => cbRef.current?.();

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [intervalMs, enabled]);
}
